import {getParameterConfigsSpecFromReadShape} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {ParameterType} from 'insights/parameters/ParameterTypes';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import trackParameters from 'insights/parameters/trackParameters';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import getParameterNamesInSQLQueries from './getParameterNamesInSQLQueries';
import {convertGQLToRESTParameterValue} from './ParameterGQLRESTConverters';

function createDefaultParameterConfig(parameterName: string): AnalyticsParameterConfigSpecRequest {
  return {
    isMulti: false,
    systemName: parameterName,
    type: ParameterType.STRING,
  };
}

/**
 * Given a SQL queries and old parameter configs, return new parameter configs that match the SQL queries
 *   - Keep old configs that are still in the SQL queries
 *   - Delete old configs that are no longer in the SQL queries
 *   - Create new configs that are in the SQL queries
 *
 * Note, given these args (not knowing the exact operations the user took), it is hard to detect a rename. If
 * "color" is renamed to "gene_color", then the old "color" config will be deleted and a new "gene_color"
 * config will be created.
 */
function syncParameterConfigsToSQLQueries(
  sqlQueries: Array<string>,
  oldConfigs: Array<AnalyticsParameterConfigGraphQL>,
  additionalConfigs?: Array<AnalyticsParameterConfigSpecRequest>
): Array<AnalyticsParameterConfigSpecRequest> {
  const oldParameterNames = oldConfigs.map((config) => config.systemName);
  const oldParameterNamesSet = new Set(oldParameterNames);
  const inUseParameterNames = getParameterNamesInSQLQueries(sqlQueries);
  const inUseParameterNamesSet = new Set(inUseParameterNames);

  const configsToKeep: Array<AnalyticsParameterConfigSpecRequest> = getParameterConfigsSpecFromReadShape(
    oldConfigs.filter((config) => inUseParameterNamesSet.has(config.systemName))
  );

  // add additionalConfigs that are now in use and not already covered by old configs
  const configsToAdd: Array<AnalyticsParameterConfigSpecRequest> = (additionalConfigs ?? []).filter(
    (config) => !oldParameterNamesSet.has(config.systemName) && inUseParameterNamesSet.has(config.systemName)
  );

  // create defaults for any other parameters that are now in use
  const paramNamesToCreate = inUseParameterNames.filter(
    (parameterName) =>
      !oldParameterNamesSet.has(parameterName) &&
      !configsToAdd.some((config) => config.systemName === parameterName)
  );
  configsToAdd.push(
    ...paramNamesToCreate.map((parameterName) => createDefaultParameterConfig(parameterName))
  );
  trackParameters(oldParameterNames, inUseParameterNames);
  return configsToKeep.concat(configsToAdd);
}

/**
 * Given new parameter configs and old parameter values, return new parameter values that match the new
 * configs
 *   - Keep old values that are still in the new configs
 *   - Delete old values that are no longer in the new configs
 */
function syncParameterValuesToNewConfigs(
  newConfigs: Array<AnalyticsParameterConfigSpecRequest>,
  oldValues: Array<AnalyticsParameterValue>
): Array<RESTAnalyticsParameterValue> {
  const definedParameterConfigIds = newConfigs
    .map((config) => config.id)
    .filter((configId) => configId != null);
  return oldValues
    .filter((value) => definedParameterConfigIds.includes(value.config.id))
    .map((value) => convertGQLToRESTParameterValue(value));
}

/**
 * Given SQL queries and old parameter configs and values, return new parameter configs and values that match
 * the SQL queries
 *   - Keep old configs and values that are still in the SQL queries
 *   - Delete old configs and values that are no longer in the SQL queries
 *   - Create new configs that are in the SQL queries, using the provided additionalConfigs, otherwise create
 *    default configs
 *
 * This is useful for the dashboard editor to sync the parameters to the dashboard's new SQL queries before
 * saving via the REST API.
 *
 * Example input:
 *   - New SQL queries: ["SELECT * FROM gene WHERE color = {{color}} AND team = {{team}}",
 *                       'SELECT * FROM "user" WHERE team = {{team}}']
 *   - Old configs: color, score
 *   - Old values: color='blue', score=5
 * Example output:
 *   - New configs: color, team
 *     - Changelog: kept "color", deleted "score", created "team"
 *   - New values: color='blue'
 *     - Changelog: kept "color", deleted "score"
 */
export default function syncParametersToSQLQueries(
  sqlQueries: Array<string>,
  oldConfigs: Array<AnalyticsParameterConfigGraphQL>,
  oldValues: Array<AnalyticsParameterValue>,
  additionalConfigs?: Array<AnalyticsParameterConfigSpecRequest>
): {
  parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
} {
  const newConfigs = syncParameterConfigsToSQLQueries(sqlQueries, oldConfigs, additionalConfigs);
  const newValues = syncParameterValuesToNewConfigs(newConfigs, oldValues);
  return {parameterConfigs: newConfigs, parameterValues: newValues};
}
